// Impersonation fetch wrapper — re-homed from the removed v1 `sdk-provider.tsx`.
// Imported for side-effect by `sdk-v2-provider.tsx`; installs once at module
// load on the client. It augments the SDK's own global `fetch` (a documented
// carve-out in `sdk-only-backend-interaction.md`), so it must cover every
// request regardless of which SDK issued it.
//
// What it does:
//   1. Attaches the `X-Morfi-Impersonate-Grant` header to every SDK request
//      when impersonation is active and the URL matches the grant's network
//      (per `attachImpersonationHeader`).
//   2. Watches responses for `IMPERSONATION_GRANT_EXPIRED` /
//      `IMPERSONATION_GRANT_INVALID` typed errors at any 401/403 and tears
//      state down so the engineer is restored to their own identity within one
//      render frame (FR-028).
//
// The wrapper is idempotent: a flag on globalThis prevents double-install
// across React StrictMode mount/unmount cycles and HMR. Kept in its own module
// (rather than inline in the provider) so it can be unit-tested in isolation.
//
import { attachImpersonationHeader } from '@/lib/impersonation';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { teardownImpersonation } from '@/lib/impersonation/teardown';

interface ImpersonationFetchGlobals {
  __morfiImpersonationFetchInstalled?: boolean;
}

const IMPERSONATION_TEARDOWN_CODES: ReadonlySet<string> = new Set([
  'IMPERSONATION_GRANT_EXPIRED',
  'IMPERSONATION_GRANT_INVALID',
]);

function installImpersonationFetchWrapper(): void {
  if (typeof window === 'undefined') return;
  const g = globalThis as typeof globalThis & ImpersonationFetchGlobals;
  if (g.__morfiImpersonationFetchInstalled === true) return;
  g.__morfiImpersonationFetchInstalled = true;

  const originalFetch: typeof fetch = window.fetch.bind(window);

  window.fetch = async function impersonationFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const wrappedInit = attachImpersonationHeader(url, mergeRequestInit(input, init));
    const response = await originalFetch(input, wrappedInit);

    if (
      !isImpersonationControlRequest(url) &&
      (response.status === 401 || response.status === 403) &&
      response.body !== null
    ) {
      // Read the response body without disturbing the consumer.
      const clone = response.clone();
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        try {
          // ALLOW-UNKNOWN: type-narrowed at use-site / mock signature passthrough.
          const parsed: unknown = await clone.json();
          if (
            parsed !== null &&
            typeof parsed === 'object' &&
            'code' in parsed &&
            // ALLOW-UNKNOWN: type-narrowed at use-site / mock signature passthrough.
            typeof (parsed as { code: unknown }).code === 'string' &&
            IMPERSONATION_TEARDOWN_CODES.has((parsed as { code: string }).code)
          ) {
            const state = useImpersonationStore.getState();
            if (state.isActive) {
              void teardownImpersonation('expired');
            }
          }
        } catch {
          // SILENT-CATCH: response body wasn't JSON or was already consumed —
          // nothing to teardown.
        }
      }
    }

    return response;
  };
}

function mergeRequestInit(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): RequestInit | undefined {
  if (!(input instanceof Request)) return init;
  const headers = new Headers(input.headers);
  if (init?.headers !== undefined) {
    new Headers(init.headers).forEach((value, key): void => {
      headers.set(key, value);
    });
  }
  return {
    ...init,
    headers,
    signal: init?.signal ?? input.signal,
  };
}

function isImpersonationControlRequest(url: string): boolean {
  try {
    const base = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const parsed = new URL(url, base);
    return (
      parsed.pathname.includes('/internal/impersonation/') ||
      (parsed.pathname.endsWith('/v1/me') && parsed.searchParams.get('kind') === 'impersonation')
    );
  } catch {
    // SILENT-CATCH: an unparseable URL cannot be an impersonation control request.
    return false;
  }
}

if (typeof window !== 'undefined') {
  installImpersonationFetchWrapper();
}
